// SignalProtocolStore — libsignal's StorageType interface backed by a key-
// value map. The default in-browser implementation persists to localStorage;
// tests construct a pure-memory variant for fast, isolated roundtrips.
//
// Why per-user namespacing matters: a single browser may briefly host more
// than one identity during a session reset (e.g. sign-out-and-wipe followed
// by sign-in as someone else). The namespace prefix keeps the data lanes
// distinct so we never decrypt one user's session record with another
// user's identity key.

import type {
  Direction,
  KeyPairType,
  SessionRecordType,
  StorageType,
} from '@privacyresearch/libsignal-protocol-typescript';

/**
 * The bytes-to-string format we use to put ArrayBuffers into JSON. Libsignal
 * keys are typically 32-byte ArrayBuffers; session records are already
 * strings (the lib serializes them for us). We base64-encode anything that
 * isn't already a string before persisting.
 */
function bufToB64(buf: ArrayBuffer): string {
  const bytes = new Uint8Array(buf);
  let bin = '';
  for (let i = 0; i < bytes.length; i++) {
    bin += String.fromCharCode(bytes[i]);
  }
  // btoa works on binary strings, which is what we just built.
  return btoa(bin);
}

function b64ToBuf(s: string): ArrayBuffer {
  const bin = atob(s);
  const out = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) {
    out[i] = bin.charCodeAt(i);
  }
  return out.buffer;
}

function keyPairToJSON(kp: KeyPairType): { pub: string; priv: string } {
  return { pub: bufToB64(kp.pubKey), priv: bufToB64(kp.privKey) };
}

function keyPairFromJSON(j: { pub: string; priv: string }): KeyPairType {
  return { pubKey: b64ToBuf(j.pub), privKey: b64ToBuf(j.priv) };
}

/**
 * The pluggable backend the store writes through. localStorage in the
 * browser, plain Map for tests.
 */
export interface KV {
  get(key: string): string | null;
  set(key: string, value: string): void;
  remove(key: string): void;
}

export const localStorageKV: KV = {
  get: (k) => localStorage.getItem(k),
  set: (k, v) => localStorage.setItem(k, v),
  remove: (k) => localStorage.removeItem(k),
};

export class MemoryKV implements KV {
  private map = new Map<string, string>();
  get(key: string): string | null {
    return this.map.get(key) ?? null;
  }
  set(key: string, value: string): void {
    this.map.set(key, value);
  }
  remove(key: string): void {
    this.map.delete(key);
  }
}

/**
 * Direction enum value the trust-on-first-use check receives. We mirror
 * libsignal's enum here so callers don't have to import it twice.
 */
export const TRUSTED_DIRECTION = {
  SENDING: 1 as Direction,
  RECEIVING: 2 as Direction,
};

export class SignalProtocolStore implements StorageType {
  /** Prefix every key with `signal.<userId>.` to scope a store per local user. */
  private readonly prefix: string;
  private readonly kv: KV;

  /**
   * @param userId  the local Supabase auth user id (uuid). Scopes the store.
   * @param kv      pluggable backend; defaults to localStorage in the browser.
   */
  constructor(userId: string, kv: KV = localStorageKV) {
    this.prefix = `signal.${userId}.`;
    this.kv = kv;
  }

  private k(suffix: string): string {
    return this.prefix + suffix;
  }

  private read<T>(suffix: string): T | undefined {
    const raw = this.kv.get(this.k(suffix));
    if (raw == null) return undefined;
    try {
      return JSON.parse(raw) as T;
    } catch {
      return undefined;
    }
  }

  private write(suffix: string, value: unknown): void {
    this.kv.set(this.k(suffix), JSON.stringify(value));
  }

  private del(suffix: string): void {
    this.kv.remove(this.k(suffix));
  }

  // ----- identity -----

  async getIdentityKeyPair(): Promise<KeyPairType | undefined> {
    const j = this.read<{ pub: string; priv: string }>('identityKey');
    return j ? keyPairFromJSON(j) : undefined;
  }

  async putIdentityKeyPair(kp: KeyPairType): Promise<void> {
    this.write('identityKey', keyPairToJSON(kp));
  }

  async getLocalRegistrationId(): Promise<number | undefined> {
    return this.read<number>('registrationId');
  }

  async putLocalRegistrationId(id: number): Promise<void> {
    this.write('registrationId', id);
  }

  /**
   * Trust-on-first-use. If we have no record of this address yet we accept
   * the key and remember it (saveIdentity does that elsewhere). If we have
   * a record, it must match — otherwise we're talking to a different
   * identity than before, which the UI should surface as a safety-number
   * change.
   */
  async isTrustedIdentity(
    identifier: string,
    identityKey: ArrayBuffer,
    _direction: Direction,
  ): Promise<boolean> {
    const stored = this.read<string>(`identity.${identifier}`);
    if (!stored) return true; // first time we see this address — accept + remember
    const incoming = bufToB64(identityKey);
    return stored === incoming;
  }

  async saveIdentity(
    encodedAddress: string,
    publicKey: ArrayBuffer,
    _nonblockingApproval?: boolean,
  ): Promise<boolean> {
    const incoming = bufToB64(publicKey);
    const stored = this.read<string>(`identity.${encodedAddress}`);
    this.write(`identity.${encodedAddress}`, incoming);
    // Return true if the identity changed (libsignal contract).
    return stored != null && stored !== incoming;
  }

  // ----- one-time prekeys -----

  async loadPreKey(keyId: string | number): Promise<KeyPairType | undefined> {
    const j = this.read<{ pub: string; priv: string }>(`preKey.${keyId}`);
    return j ? keyPairFromJSON(j) : undefined;
  }

  async storePreKey(keyId: number | string, keyPair: KeyPairType): Promise<void> {
    this.write(`preKey.${keyId}`, keyPairToJSON(keyPair));
  }

  async removePreKey(keyId: number | string): Promise<void> {
    this.del(`preKey.${keyId}`);
  }

  // ----- signed prekey -----

  async loadSignedPreKey(keyId: string | number): Promise<KeyPairType | undefined> {
    const j = this.read<{ pub: string; priv: string }>(`signedPreKey.${keyId}`);
    return j ? keyPairFromJSON(j) : undefined;
  }

  async storeSignedPreKey(
    keyId: number | string,
    keyPair: KeyPairType,
  ): Promise<void> {
    this.write(`signedPreKey.${keyId}`, keyPairToJSON(keyPair));
  }

  async removeSignedPreKey(keyId: number | string): Promise<void> {
    this.del(`signedPreKey.${keyId}`);
  }

  // ----- sessions -----

  async loadSession(encodedAddress: string): Promise<SessionRecordType | undefined> {
    return this.read<string>(`session.${encodedAddress}`);
  }

  async storeSession(encodedAddress: string, record: SessionRecordType): Promise<void> {
    this.write(`session.${encodedAddress}`, record);
  }
}
